import "./App.css";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Aside from "./Aside";
import { Col, Container, Row } from "react-bootstrap";
import Filters from "./Filters";

import Map from "./Map";
import loaderImg from "./images/loader.svg";
import mustRotateImg from "./images/must-rotate.svg";
import logoTc from "./images/logo-thecamp-cip.png";
import TopNav from "./TopNav.js";

import FilterPills from "./FilterPills";
import Restauration from "./Restauration";
import SimpleBar from "simplebar-react";
import "simplebar/dist/simplebar.min.css";
import { fetchData } from "./services/api/FetchData";
import { sortItems } from "./utils/SortItems";
import { checkParamURLWithParameter } from "./utils/checkParamURL";
import { getRestaurationTarget, hasRestauration } from "./utils/restauration";
import { FILTERS, FILTER_LAYER_MAP } from "./constants/filters";
import { MOCK_TC_PLACES } from "./mock/MOCK_PLACES.js";

export const ModePanelContext = React.createContext();
export const isMobileContext = React.createContext();

const USEMOCK = false;

export const SHOW_CHASSE_AUX_SAVEURS = (placesFetched) => {
    const layerId = FILTER_LAYER_MAP[FILTERS.CHASSE_AUX_SAVEURS];
    return placesFetched.some(place => place.layer === layerId);
};

const endpoint = process.env.REACT_APP_API_ENDPOINT;

/* L'URL suit la fiche ouverte : le lien se partage, et le bouton restauration
   renvoie une adresse qui rouvre le même espace. */
const setPlaceParam = (mapId) => {
    const url = new URL(window.location.href);
    if (mapId) {
        url.searchParams.set("place", mapId);
    } else {
        url.searchParams.delete("place");
    }
    window.history.replaceState(null, "", url);
};

function App() {
    const scrolRef = useRef();
    // Lieu à ouvrir dès que la liste du filtre correspondant sera chargée
    // (cf. openRestauration : la fiche demandée n'est pas dans le filtre actif).
    const pendingPlaceRef = useRef(null);
    /* Lu une fois au tout premier rendu : `returnToList` efface le paramètre
       dès que les données arrivent, donc le lien profond doit être mis de côté
       avant. */
    const deepLinkRef = useRef(checkParamURLWithParameter("place"));

    const [loading, setloading] = useState(true);

    const [isMobile, setIsMobile] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);

    const [placesFetched, setplacesFetched] = useState([]);
    const [modeList, setModeList] = useState("");
    const [listeFiltered, setListeFiltered] = useState([]);

    const [isExtendedCard, setIsExtendedCard] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState([]);

    const showChasseAuxSaveurs = SHOW_CHASSE_AUX_SAVEURS(placesFetched);
    // Pour l'affichage du bouton seulement ; la cible, elle, se recalcule au
    // clic pour ne pas dépendre de l'heure du dernier rendu.
    const showRestaurationButton = useMemo(
        () => hasRestauration(placesFetched),
        [placesFetched],
    );

    const hover = (Id) => {
        const element = document.getElementById(`place-${Id}`);
        if (element && element.firstElementChild) {
            element.firstElementChild.classList.add("hovered");
        }
    };
    const outHover = (Id) => {
        const element = document.getElementById(`place-${Id}`);
        if (element && element.firstElementChild) {
            element.firstElementChild.classList.remove("hovered");
        }
    };

    const changeFilter = useCallback((filterName) => {
        setListeFiltered([]);
        setModeList(filterName);
        const url = new URL(window.location.href);
        url.searchParams.set('filter', filterName);
        window.history.replaceState(null, '', url);
    }, []);

    const filterPlaces = useCallback(() => {
        const layerId = FILTER_LAYER_MAP[modeList];
        if (!layerId) return;
        setListeFiltered(placesFetched.filter(place => place.layer === layerId));
    }, [placesFetched, modeList]);

    const extendByURL = useCallback(
        (placeName) => {
            let filterPlace = placesFetched.filter((filtre) => {
                if (filtre["map-id"] === placeName) {
                    return true;
                }
                return false;
            });
            if (filterPlace.length === 0) return;
            setSelectedPlace(filterPlace);
            setIsExtendedCard(true);
            setPlaceParam(placeName);
        },
        [placesFetched],
    );

    const displayUrlPlaces = useCallback(() => {
        const placeParam = deepLinkRef.current;
        if (!placeParam) return;
        // Consommé une seule fois : revenir à la liste ne doit pas rouvrir la
        // fiche d'origine.
        deepLinkRef.current = null;
        extendByURL(placeParam);
    }, [extendByURL]);

    const load = useCallback(async () => {
        try {
            let data = USEMOCK ? MOCK_TC_PLACES : await fetchData(endpoint);
            // Filter out places where _draft or _archived is true
            data = data.filter(place => !(place._draft === true || place._archived === true));
            const sortedPlaces = sortItems(data);
            setplacesFetched(sortedPlaces);
            setloading(false);
        } catch (error) {
            setloading(true);
            alert(
                "Le serveur distant est injoignable veuillez réessayer ultérieuremnt",
            );
            console.log(error);
        }
    }, []);

    const handleResize = useCallback(() => {
        if (width < 992 && window.innerWidth > 992) {
            setloading(true);
            window.location.reload();
        } else if (width > 992 && window.innerWidth < 992) {
            setloading(true);
            window.location.reload();
        } else {
            setWidth(window.innerWidth);
        }
    }, [width]);

    const mapInitialised = () => {
        localStorage.setItem("scrollHistory", 0);
        setTimeout(() => {
            setWidth(window.innerWidth);
        }, 1000);
    };

    const saveScrollPosition = useCallback(() => {
        if (isExtendedCard === false) {
            scrolRef.current.recalculate();
            localStorage.setItem(
                "scrollHistory",
                scrolRef.current.getScrollElement().scrollTop,
            );
        }
        setTimeout(() => {
            scrolRef.current.recalculate();
            scrolRef.current.getScrollElement().scrollTop = 0;
        }, 500);
    }, [isExtendedCard]);

    const returnToList = useCallback(() => {
        setIsExtendedCard(false);
        setSelectedPlace([]);
        setPlaceParam(null);
        scrolRef.current.recalculate();
        false === isMobile
            ? setTimeout(() => {
                  scrolRef.current
                      .getScrollElement()
                      .scrollTo(
                          localStorage.getItem("scrollHistory"),
                          localStorage.getItem("scrollHistory"),
                      );
                  localStorage.setItem("scrollHistory", 0);
              }, 220)
            : setTimeout(() => {
                  scrolRef.current
                      .getScrollElement()
                      .scrollTo(
                          localStorage.getItem("scrollHistory"),
                          localStorage.getItem("scrollHistory"),
                      );
                  localStorage.setItem("scrollHistory", 0);
              }, 500);
    }, [isMobile]);

    const openPlaceCard = useCallback(
        (place) => {
            setSelectedPlace([place]);
            setIsExtendedCard(true);
            setPlaceParam(place["map-id"]);
            saveScrollPosition();
        },
        [saveScrollPosition],
    );

    /* Le bouton « Pour me restaurer » : on ouvre la fiche de l'espace qui sert
       à cet instant — ou, hors service, du prochain à ouvrir.

       L'heure est lue maintenant et pas au rendu : sur un écran du campus,
       l'application peut tourner plusieurs jours sans recharger.

       La fiche s'ouvre depuis la liste qui la contient, jamais à côté d'elle :
       si le filtre doit changer, l'ouverture attend que la nouvelle liste soit
       calculée (effet ci-dessous). Le filtre est déduit du calque du lieu —
       « espaces » en pratique — plutôt que codé en dur, pour que le bouton
       continue de fonctionner si un espace de restauration change de calque
       dans Webflow. */
    const openRestauration = useCallback(() => {
        const target = getRestaurationTarget(placesFetched, new Date());
        if (!target) return;

        if (listeFiltered.some((item) => item["map-id"] === target.mapId)) {
            openPlaceCard(target.place);
            return;
        }

        const filterName = Object.keys(FILTER_LAYER_MAP).find(
            (name) => FILTER_LAYER_MAP[name] === target.place.layer,
        );
        if (!filterName) return;

        pendingPlaceRef.current = target.mapId;
        changeFilter(filterName);
    }, [placesFetched, listeFiltered, changeFilter, openPlaceCard]);

    useEffect(() => {
        if (!pendingPlaceRef.current) return;
        const place = listeFiltered.find(
            (item) => item["map-id"] === pendingPlaceRef.current,
        );
        if (!place) return;
        pendingPlaceRef.current = null;
        openPlaceCard(place);
    }, [listeFiltered, openPlaceCard]);

    useEffect(() => {
        if (width < 992) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [width]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        if (placesFetched.length !== 0) {
            returnToList();
            filterPlaces();
        }
    }, [placesFetched.length, filterPlaces, returnToList]);

    useEffect(() => {
        if (!loading) {
            displayUrlPlaces();
        }
    }, [loading, displayUrlPlaces]);

    useEffect(() => {
        load();
        return () => {
            localStorage.clear();
        };
    }, [load]);

    useEffect(() => {
        if (placesFetched.length === 0) return;
        const filterParam = checkParamURLWithParameter('filter');
        let activeFilter;
        switch (filterParam) {
            case FILTERS.ECO_CONCEPTION:
                activeFilter = FILTERS.ECO_CONCEPTION;
                break;
            case FILTERS.CHASSE_AUX_SAVEURS:
                activeFilter = FILTERS.CHASSE_AUX_SAVEURS;
                break;
            default:
                activeFilter = FILTERS.ESPACES;
        }
        changeFilter(activeFilter);
    }, [placesFetched.length, changeFilter]);

    const extendsCard = (cardId) => {
        setSelectedPlace([]);
        if (cardId.includes("place")) {
            cardId = cardId.slice(6);
        }
        if (false === isMobile) {
            const element = document.getElementById(`place-${cardId}`);
            if (element && element.firstElementChild) {
                element.firstElementChild.removeAttribute("class");
            }
        }
        let filterPlace = listeFiltered.filter((filtre) => {
            if (filtre["map-id"] === cardId) {
                return true;
            } else {
                const element = document.getElementById(
                    `place-${filtre["map-id"]}`,
                );
                if (element && element.firstElementChild) {
                    element.firstElementChild.classList.remove("hovered");
                }
                return false;
            }
        });
        if (filterPlace.length === 0) return;
        setSelectedPlace(filterPlace);
        setIsExtendedCard(true);
        setPlaceParam(cardId);
        saveScrollPosition();
    };

    // Pas de bouton si Webflow ne déclare aucun horaire lisible ; sur mobile il
    // occupe le haut de l'écran, donc on l'efface au-dessus d'une fiche.
    const showRestauration =
        showRestaurationButton && (!isMobile || !isExtendedCard);

    return (
        <>
            <isMobileContext.Provider value={isMobile}>
                <Container
                    fluid
                    className={
                        isMobile && showRestauration ? "has-restauration" : undefined
                    }
                >
                    <div className="must-rotate">
                        <p>veuillez tournez votre t&#233;l&#233;phone</p>
                        <img
                            alt="Must rotate your device"
                            src={mustRotateImg}
                        />
                    </div>
                    {loading && (
                        <div className="loader">
                            <img alt="loader" src={loaderImg} />
                        </div>
                    )}
                    {!isMobile && (
                        <a href="/">
                            {" "}
                            <img src={logoTc} className="logo" alt="Logo" />
                        </a>
                    )}
                    {/* Hors du Row : Bootstrap force width:100% sur `.row > *` */}
                    {isMobile && !isExtendedCard && (
                        <FilterPills
                            filters={modeList}
                            changeFilter={changeFilter}
                            showChasseAuxSaveurs={showChasseAuxSaveurs}
                        />
                    )}
                    {isMobile && showRestauration && (
                        <Restauration onFindPlace={openRestauration} />
                    )}
                    <Row>
                        {isMobile && <TopNav />}
                        <Col xs="12" lg="8" className="px-0 map-col">
                            {!isMobile && (
                                <>
                                    <Map
                                        isExtended={isExtendedCard}
                                        listToDisplay={listeFiltered}
                                        extendCard={extendsCard}
                                        selectedPlace={selectedPlace}
                                        mapInitialised={mapInitialised}
                                        filters={modeList}
                                    />
                                    <Filters
                                        changeFilter={changeFilter}
                                        filters={modeList}
                                        showChasseAuxSaveurs={showChasseAuxSaveurs}
                                    />
                                    {showRestauration && (
                                        <Restauration
                                            onFindPlace={openRestauration}
                                        />
                                    )}
                                </>
                            )}
                        </Col>
                        <Col
                            xs="12"
                            lg="4"
                            className={`aside-panel ${isMobile ? (!isExtendedCard ? "mobile-visible px-0" : "extended-mobiles mt-0 px-0 mobile-visible ") : "separation px-0"}`}
                        >
                            <SimpleBar
                                style={{ height: "100vh" }}
                                autoHide={true}
                                forceVisible="n"
                                timeout="400"
                                ref={scrolRef}
                            >
                                <Aside
                                    filters={modeList}
                                    listToDisplay={listeFiltered}
                                    backToList={returnToList}
                                    isExtended={isExtendedCard}
                                    goExtendCard={extendsCard}
                                    selectedPlace={selectedPlace}
                                    hover={hover}
                                    outHover={outHover}
                                />
                            </SimpleBar>
                        </Col>
                    </Row>
                </Container>
            </isMobileContext.Provider>
        </>
    );
}
export default App;
